import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import GroupForm from "../../components/GroupForm/GroupForm";
import useAdminGroups from "../../hooks/useAdminGroups";
import useEditGroup from "../../hooks/useEditGroup";
import useDeleteGroup from "../../hooks/useDeleteGroup";
import { adaptGroupPayload } from "../../adapters/adaptGroupPayload";

const EditGroup = () => {
	const navigate = useNavigate();
	const { groupId } = useParams<{ groupId: string }>();
	const id = Number(groupId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<GroupFormValues>({
		name: "",
		description: ""
	});

	const { data: allGroups = [] } = useAdminGroups();
	const group = allGroups.find((g) => g.id === id);

	const { mutate: editGroup, isLoading: isEditing } = useEditGroup();
	const { mutate: deleteGroup, isLoading: isDeleting } = useDeleteGroup();

	useEffect(() => {
		if (group) {
			setDefaultValues({
				name: group.name,
				description: group.description ?? ""
			});
		}
	}, [group]);

	if (!isValidId) {
		return <Navigate to="/users/groups" replace />;
	}

	const handleSubmit = (data: GroupFormValues) => {
		const payload = adaptGroupPayload(data);

		editGroup(
			{ id, payload },
			{
				onSuccess: () => navigate("/users/groups")
			}
		);
	};

	const handleDelete = () => {
		deleteGroup(id, {
			onSuccess: () => {
				setShowDeleteModal(false);
				navigate("/users/groups");
			}
		});
	};

	return (
		<Page>
			<GroupForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				backPath="/users/groups"
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete group?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setShowDeleteModal(false);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditGroup;
